import React from 'react';
import { IconX, IconSparkles } from '@tabler/icons-react';
import MDialog from '@/components/ui/atoms/MDialog';
import styles from './MAIModal.module.css';

export type AIModalVariant = 'prescription' | 'labReport';

interface MAIModalProps {
  open: boolean;
  onClose: () => void;
  content?: string;
  whatsNextTitle?: string;
  whatsNextContent?: string | string[];
  variant?: 'prescription' | 'labReport';
  insightsData?: any[];
}

// Helper function to check if content should be hidden
const shouldHideContent = (text?: string): boolean => {
  if (!text || text.trim() === '') return true;
  
  // Normalize: lowercase, trim, remove punctuation at the end
  const normalizedText = text.toLowerCase().trim().replace(/[.,;:!?]+$/, '');
  
  // Exact match patterns - only hide if the entire content is exactly one of these
  const exactMatchPatterns = [
    'not available',
    'not clearly mentioned',
    'not mentioned',
    'not specified',
    'information not available',
    'data not available',
    'not provided',
    'n/a',
    'na',
    'none',
    'nil',
    'not applicable',
    'not found'
  ];
  
  // Check for exact matches only
  return exactMatchPatterns.includes(normalizedText);
};

const MAIModal: React.FC<MAIModalProps> = ({
  open,
  onClose,
  content,
  whatsNextTitle,
  whatsNextContent,
  variant = 'prescription',
  insightsData,
}) => {
  return (
    <MDialog isOpen={open} onClose={onClose} showCloseButton={false} title="" className="noPadding">
      <div className={styles.aimodalContainer}>
        {/* Header Section */}
        <div className={styles.headerSection}>
          {/* Top section with close icon */}
          <div className={styles.topSection}>
            <button 
              className={styles.closeButton}
              onClick={onClose}
              type="button"
              aria-label="Close"
            >
              <IconX size={20} color="#FFFFFF" />
            </button>
          </div>

          {/* Icon and title horizontally aligned */}
          <div className={styles.iconTitleContainer}>
            <div className={styles.iconContainer}>
              <IconSparkles size={12} color="#4C189A" />
            </div>
            <h2 className={styles.aiPreviewText}>AI Preview</h2>
          </div>
        </div>

        {/* Body Section */}
        <div className={styles.bodySection}>
          {(() => {
            if (variant === 'labReport') {
              return <LabReportContent insightsData={insightsData} />;
            }
            if (insightsData?.find(item => item.type === 'DISCHARGE_SUMMARY_INSIGHT')) {
              return <DischargeSummaryContent insightsData={insightsData} />;
            }
            return (
              <AIContent 
                content={content}
                whatsNextTitle={whatsNextTitle}
                whatsNextContent={whatsNextContent}
              />
            );
          })()}
        </div>

        {/* Footer Section */}
        <div className={styles.footerSection}>
          <p className={styles.footerText}>AI-generated insights may occasionally be inaccurate. Refer to the original document.</p>
        </div>
      </div>
    </MDialog>
  );
};

interface LabReportContentProps {
  insightsData?: any[];
}

const LabReportContent: React.FC<LabReportContentProps> = ({ insightsData }) => {
  // Check for LAB_SUMMARY_PROCEDURE first
  const labSummaryProcedure = insightsData?.find(item => item.type === 'LAB_SUMMARY_PROCEDURE');
  
  if (labSummaryProcedure) {
    const { indication, procedure_performed, key_findings, next_steps } = labSummaryProcedure.value;

    return (
      <>
        {/* Indication section */}
        {indication && !shouldHideContent(indication) && (
          <div className={styles.contentSection}>
            <h3 className={styles.sectionTitle}>Indication:</h3>
            <p className={styles.sectionContent}>{indication}</p>
          </div>
        )}

        {/* Procedure Performed section */}
        {procedure_performed && !shouldHideContent(procedure_performed) && (
          <div className={styles.contentSection}>
            <h3 className={styles.sectionTitle}>Procedure Performed:</h3>
            <p className={styles.sectionContent}>{procedure_performed}</p>
          </div>
        )}

        {/* Key Findings section */}
        {key_findings && !shouldHideContent(key_findings) && (
          <div className={styles.contentSection}>
            <h3 className={styles.sectionTitle}>Key Findings:</h3>
            <p className={styles.sectionContent}>{key_findings}</p>
          </div>
        )}

        {/* What's Next section */}
        {!shouldHideContent(next_steps) && (
          <div className={styles.contentSection}>
            <h3 className={styles.sectionTitle}>What's Next:</h3>
            {next_steps ? (
              <p className={styles.sectionContent}>{next_steps}</p>
            ) : (
              <p className={styles.sectionContent}>Keep reports carefully and bring them during the next hospital visit.</p>
            )}
          </div>
        )}
      </>
    );
  }

  // Check for LAB_SUMMARY_IMAGING next
  const labSummaryImaging = insightsData?.find(item => item.type === 'LAB_SUMMARY_IMAGING');
  
  if (labSummaryImaging) {
    const { body_part_imaged, test_method, key_findings, clinical_impression } = labSummaryImaging.value;

    return (
      <>
        {/* Body Part Imaged section */}
        {body_part_imaged && !shouldHideContent(body_part_imaged) && (
          <div className={styles.contentSection}>
            <h3 className={styles.sectionTitle}>Body Part Imaged:</h3>
            <p className={styles.sectionContent}>{body_part_imaged}</p>
          </div>
        )}

        {/* Test Method section */}
        {test_method && !shouldHideContent(test_method) && (
          <div className={styles.contentSection}>
            <h3 className={styles.sectionTitle}>Test Method:</h3>
            <p className={styles.sectionContent}>{test_method}</p>
          </div>
        )}

        {/* Key Findings section */}
        {key_findings && !shouldHideContent(key_findings) && (
          <div className={styles.contentSection}>
            <h3 className={styles.sectionTitle}>Key Findings:</h3>
            <p className={styles.sectionContent}>{key_findings}</p>
          </div>
        )}

        {/* Clinical Impression section */}
        {!shouldHideContent(clinical_impression) && (
          <div className={styles.contentSection}>
            <h3 className={styles.sectionTitle}>Clinical Impression:</h3>
            {clinical_impression ? (
              <p className={styles.sectionContent}>{clinical_impression}</p>
            ) : (
              <p className={styles.sectionContent}>Clinical correlation advised.</p>
            )}
          </div>
        )}
      </>
    );
  }

  // Check for LAB_SUMMARY_ALLERGY next
  const labSummaryAllergy = insightsData?.find(item => item.type === 'LAB_SUMMARY_ALLERGY');
  
  if (labSummaryAllergy) {
    const { test_method, allergen_panel, positive_allergens, recommendations } = labSummaryAllergy.value;

    return (
      <>
        {/* Test Method section */}
        {test_method && !shouldHideContent(test_method) && (
          <div className={styles.contentSection}>
            <h3 className={styles.sectionTitle}>Test Method:</h3>
            <p className={styles.sectionContent}>{test_method}</p>
          </div>
        )}

        {/* Allergen Panel section */}
        {allergen_panel && !shouldHideContent(allergen_panel) && (
          <div className={styles.contentSection}>
            <h3 className={styles.sectionTitle}>Allergen Panel:</h3>
            <p className={styles.sectionContent}>{allergen_panel}</p>
          </div>
        )}

        {/* Positive Allergens section */}
        {positive_allergens && !shouldHideContent(positive_allergens) && (
          <div className={styles.contentSection}>
            <h3 className={styles.sectionTitle}>Positive Allergens:</h3>
            <p className={styles.sectionContent}>{positive_allergens}</p>
          </div>
        )}

        {/* Recommendations section */}
        {!shouldHideContent(recommendations) && (
          <div className={styles.contentSection}>
            <h3 className={styles.sectionTitle}>Recommendations:</h3>
            {recommendations ? (
              <p className={styles.sectionContent}>{recommendations}</p>
            ) : (
              <p className={styles.sectionContent}>Consult with your healthcare provider for recommendations.</p>
            )}
          </div>
        )}
      </>
    );
  }

  // Helper function to format parameter names by adding space after commas if missing
  const formatParameterName = (name: string) => {
    return name.replace(/,([^\s])/g, ', $1');
  };

  // Process lab report data to extract abnormal values
  const abnormalValues: Array<{
    parameter_name: string;
    parameter_result: string;
    parameter_unit: string;
    direction: string;
  }> = [];

  if (insightsData && Array.isArray(insightsData)) {
    insightsData.forEach(item => {
      if (item.type === 'LAB_SUMMARY' && item.value?.abnormal_values) {
        item.value.abnormal_values.forEach((factor: any) => {
          if (factor.severity === 'low' || factor.severity === 'high' || factor.severity === 'medium') {
            abnormalValues.push({
              parameter_name: factor.parameter_name,
              parameter_result: factor.parameter_result,
              parameter_unit: factor.parameter_unit,
              direction: factor.direction
            });
          }
        });
      }
    });
  }

  return (
    <>
      {/* At a glance section */}
      <div className={styles.contentSection}>
        <p className={styles.sectionContent}>
          {abnormalValues.length > 0 
            ? `This report shows ${abnormalValues.length} values outside the range`
            : "All values are within the range in this report."
          }
        </p>
      </div>

      {/* Lab Report Table */}
      <div className={styles.labReportTable}>
        <h3 className={styles.labReportTitle}>Values outside the range</h3>
        <div className={`${styles.labReportValues} ${abnormalValues.length === 0 ? styles.noValues : ''}`}>
          {abnormalValues.length > 0 ? (
            <>
              {/* Show first 6 values */}
              {abnormalValues.slice(0, 6).map((value, index) => (
                <div key={`lab-value-${value.parameter_name.slice(0, 20).replace(/\s+/g, '-')}-${index}`} className={styles.labReportRow}>
                  <span className={styles.labReportParameter}>{formatParameterName(value.parameter_name)}</span>
                  <span className={styles.labReportResult}>
                    {value.parameter_result} {value.parameter_unit}
                  </span>
                  <span className={styles.labReportSeverity}>
                    ({value.direction})
                  </span>
                </div>
              ))}
              
              {/* Show "and X others" if there are more than 6 values */}
              {abnormalValues.length > 6 && (
                <div className={styles.labReportRow}>
                  <span className={styles.labReportOthers}>
                    and {abnormalValues.length - 6} others
                  </span>
                </div>
              )}
            </>
          ) : (
            <div className={styles.labReportRow}>
              <span className={styles.labReportParameter}>None</span>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

interface DischargeSummaryContentProps {
  insightsData?: any[];
}

const DischargeSummaryContent: React.FC<DischargeSummaryContentProps> = ({ insightsData }) => {
  const dischargeSummary = insightsData?.find(item => item.type === 'DISCHARGE_SUMMARY_INSIGHT');
  
  if (dischargeSummary) {
    const { reason_for_admission, key_diagnosis, treatment_procedure_done, discharge_advice_followup } = dischargeSummary.value;

    return (
      <>
        {/* Reason for Admission section */}
        {reason_for_admission && !shouldHideContent(reason_for_admission) && (
          <div className={styles.contentSection}>
            <h3 className={styles.sectionTitle}>Reason for Admission:</h3>
            <p className={styles.sectionContent}>{reason_for_admission}</p>
          </div>
        )}

        {/* Key Diagnosis section */}
        {key_diagnosis && !shouldHideContent(key_diagnosis) && (
          <div className={styles.contentSection}>
            <h3 className={styles.sectionTitle}>Key Diagnosis:</h3>
            <p className={styles.sectionContent}>{key_diagnosis}</p>
          </div>
        )}

        {/* Treatment/Procedure Done section */}
        {treatment_procedure_done && !shouldHideContent(treatment_procedure_done) && (
          <div className={styles.contentSection}>
            <h3 className={styles.sectionTitle}>Treatment/Procedure Done:</h3>
            <p className={styles.sectionContent}>{treatment_procedure_done}</p>
          </div>
        )}

        {/* What's Next section */}
        {!shouldHideContent(discharge_advice_followup) && (
          <div className={styles.contentSection}>
            <h3 className={styles.sectionTitle}>What's Next:</h3>
            {discharge_advice_followup ? (
              <p className={styles.sectionContent}>{discharge_advice_followup}</p>
            ) : (
              <p className={styles.sectionContent}>Follow up with your healthcare provider as advised.</p>
            )}
          </div>
        )}
      </>
    );
  }

  // Fallback to parsing summary if structured data is not available
  return <AIContent content={dischargeSummary?.summary} whatsNextTitle="What's Next" whatsNextContent="" />;
};

interface ContentProps {
  content?: string;
  whatsNextTitle?: string;
  whatsNextContent?: string | string[];
}

const AIContent: React.FC<ContentProps> = ({
  content,
  whatsNextTitle = "What's next",
  whatsNextContent,
}) => {
  // Helper function to parse content and extract sections with subheadings
  const parseContentIntoSections = (text: string) => {
    // Split on both actual newlines and the literal string '\n' from API
    const lines = text.split(/\\n|\n/).filter(line => line.trim());
    
    const sections: Array<{
      subheading: string;
      content: string[];
    }> = [];
    
    let currentSection: { subheading: string; content: string[] } | null = null;
    
    lines.forEach(line => {
      const trimmedLine = line.trim();
      
      // Check if this line is a heading (ends with colon)
      if (trimmedLine.endsWith(':')) {
        // Save previous section if exists
        if (currentSection) {
          sections.push(currentSection);
        }
        // Replace "Symptoms / Reason for Visit:" with "Reason for visit:"
        const normalizedSubheading = trimmedLine === 'Symptoms / Reason for Visit:' 
          ? 'Reason for visit:' 
          : trimmedLine;
        // Start new section
        currentSection = {
          subheading: normalizedSubheading,
          content: []
        };
      } else if (currentSection) {
        // Add content to current section
        currentSection.content.push(trimmedLine);
      } else {
        // If no section started yet, create a default one
        currentSection = {
          subheading: '',
          content: [trimmedLine]
        };
      }
    });
    
    // Add the last section
    if (currentSection) {
      sections.push(currentSection);
    }
    
    return sections;
  };

  const sections = content ? parseContentIntoSections(content) : [];

  return (
    <>
      {/* Render each section with subheading styled like "At a glance" */}
      {sections.map((section, index) => (
        <div key={`section-${section.subheading || 'default'}-${index}`} className={styles.contentSection}>
          {section.subheading && (
            <h3 className={styles.sectionTitle}>{section.subheading}</h3>
          )}
          {section.content.map((contentLine, lineIndex) => (
            <p key={`content-${section.subheading || 'default'}-${lineIndex}-${contentLine.slice(0, 10).replace(/\s+/g, '-')}`} className={styles.sectionContent}>
              {contentLine}
            </p>
          ))}
        </div>
      ))}

      {/* Bottom section - What's next */}
      <div className={styles.contentSection}>
        <h3 className={styles.sectionTitle}>{whatsNextTitle}</h3>
        {whatsNextContent && (
          <>
            {Array.isArray(whatsNextContent) ? (
              whatsNextContent.map((content, index) => (
                <p key={`whats-next-content-${content.slice(0, 20).replace(/\s+/g, '-')}-${index}`} className={styles.sectionContent}>{content}</p>
              ))
            ) : (
              <>
                {parseContentIntoSections(whatsNextContent).map((section, sectionIndex) => (
                  <div key={`whats-next-section-${section.subheading || 'default'}-${sectionIndex}`}>
                    {section.content.map((contentLine, lineIndex) => (
                      <p key={`whats-next-content-${section.subheading || 'default'}-${lineIndex}-${contentLine.slice(0, 10).replace(/\s+/g, '-')}`} className={styles.sectionContent}>
                        {contentLine}
                      </p>
                    ))}
                  </div>
                ))}
              </>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default MAIModal;
